import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";

// Token getter function that will be set by AuthContext
let getTokenFn: (() => Promise<string | null>) | null = null;
let logoutFn: (() => void) | null = null;

// Function to set the token getter (called from AuthContext)
export const setTokenGetter = (fn: () => Promise<string | null>) => {
  getTokenFn = fn;
};

// Function to set the logout function (called from AuthContext)
export const setLogoutFunction = (fn: () => void) => {
  logoutFn = fn;
};

// Create a base query with Auth0 integration
const createBaseQuery = (): BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> => {
  return fetchBaseQuery({
    baseUrl: import.meta.env.VITE_API_BASE_URL || "",
    prepareHeaders: async (headers, api) => {
      try {
        // Get Auth0 token using the getter function
        if (getTokenFn) {
          const token = await getTokenFn();

          if (token) {
            headers.set("Authorization", `Bearer ${token}`);
            console.log("🔐 RTK Query: Headers prepared with Auth0 token");
          } else {
            console.log("⚠️ RTK Query: No Auth0 token available");
          }
        } else {
          console.log("⚠️ RTK Query: Token getter not initialized");
        }

        // FormData bodies (e.g. file uploads) must let the browser set its own
        // multipart Content-Type with boundary — forcing application/json here
        // would break the request.
        const isFormDataBody =
          typeof api.arg === "object" &&
          api.arg !== null &&
          "body" in api.arg &&
          api.arg.body instanceof FormData;

        if (!isFormDataBody) {
          headers.set("Content-Type", "application/json");
        }
      } catch (error) {
        console.error("❌ RTK Query: Error preparing headers:", error);
      }

      return headers;
    },
  });
};

export const baseQuery = createBaseQuery();

// Enhanced base query with retry logic
export const baseQueryWithRetry: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  // Retry on 401 (unauthorized) - token might have expired
  if (result.error && result.error.status === 401) {
    console.log("🔄 RTK Query: Retrying request after 401 error");

    // Wait a bit for token refresh
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Retry the request
    result = await baseQuery(args, api, extraOptions);

    // If still 401 after retry, logout the user
    if (result.error && result.error.status === 401) {
      console.log("❌ RTK Query: Still 401 after retry, logging out user");

      if (logoutFn) {
        setTimeout(() => {
          logoutFn!();
        }, 100);
      }
    }
  }

  return result;
};
